import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useUserApiData } from "../../api/userApi";
import { useOktaAuth } from "@okta/okta-react";
import { setCurrentSectorFilter, setUserData } from "../../store/userSlice";
import {
  resetForecastState,
  resetRcaState,
  resetResultVisualizationExpState,
  resetResultVisualizationState,
  setRcaFilter,
} from "../../store/filterSlice";
import { resetForecastData } from "../../store/foreCastDataSlice";
import { resetForecastDecomState } from "../../store/foreCastDecomSlice";
import {
  resetForecastBackTestingApiDataLoading,
  resetForecastBackTestingAverageState,
  resetForecastBackTestingChartState,
  resetForecastBackTestingTableState,
} from "../../store/foreCastBackTestingDataSlice";
import { useForecastClusterFilter } from "../../api/filtersApi";
import { RootState } from "../../store/store";
import {
  resetExperId,
  resetFFChartState,
  resetResultVisulizationExperimentBackTestingTableDataSetter,
  resetResultVisulizationExperimentForwardForecastDataSetter,
  setLoader,
} from "../../store/resultVisualizationExperimentation";
import {
  resetPredefinePaginationState,
  resetresultVisulizationBackTestingTableState,
  resetResultVisulizationChartData,
} from "../../store/resultVisualizationPredefined";
import {
  resetMetrics,
  resetTableData,
  resetTreeChartData,
} from "../../store/rcaSlice";

export const Sector = () => {
  const storedSelection = JSON.parse(
    localStorage.getItem("currentSelection") || "{}"
  );
  const [selectedSector, setSelectedSector] = useState(
    storedSelection.sector || ""
  );
  const [selectedMarket, setSelectedMarket] = useState(
    storedSelection.market || ""
  );

  const [defaultMarket, setDefaultMarket] = useState("");
  const [defaultSector, setDefaultSector] = useState("");
  const [sectorChangeEvent, setSectorChangeEvent] = useState(false);
  const [sector, setSector] = useState<string[]>([]);
  const [market, setMarket] = useState<string[]>([]);
  const [sectorName, setSectorName] = useState<string>("");
  const dispatch = useDispatch();
  const { authState } = useOktaAuth();
  const currentSectorData = useMemo(() => {
    return {
      sector: selectedSector ? selectedSector : defaultSector,
      market: selectedMarket ? selectedMarket : defaultMarket,
    };
  }, [selectedSector, defaultSector, selectedMarket, defaultMarket]);
  const { data: userApiData } = useUserApiData(
    authState?.accessToken?.accessToken!,
    "userdata",
    currentSectorData || {}
  );
  const userData: any = useSelector((state: RootState) => state.user);
  const { data: clusterApiData } = useForecastClusterFilter(
    authState?.accessToken?.accessToken!,
    "cluster",
    userData?.currentSector ? userData.currentSector : {}
  );

  useEffect(() => {
    if (!userApiData) return;
    dispatch(setUserData(userApiData));
    const { filterData, userSectorData } = userApiData;

    if (!selectedSector) setSelectedSector(userSectorData?.defaultSector || "");
    setSector(userSectorData?.sectors || []);
    setMarket(userSectorData?.markets || []);
    setDefaultMarket(userSectorData?.defaultMarket || "");
    setDefaultSector(userSectorData?.defaultSector || "");

    if (filterData) {
      localStorage.setItem(
        `userData-${
          selectedSector ? selectedSector : userSectorData?.defaultSector
        }`,
        JSON.stringify(userApiData)
      );
    }

    let sectorData = selectedSector || userSectorData?.defaultSector;
    let marketData = !sectorChangeEvent
      ? selectedMarket
        ? selectedMarket
        : userSectorData?.defaultMarket
      : userSectorData?.defaultMarket;

    let pbna_check =
      userSectorData?.markets && userSectorData?.markets[0]?.includes("PBNA");
    let pfna_check =
      userSectorData?.markets && userSectorData?.markets[0]?.includes("PFNA");

    if (pbna_check) {
      setSectorName("PBNA");
    } else if (pfna_check) {
      setSectorName("PFNA");
    } else {
      setSectorName("LATAM");
    }

    if (sectorData && marketData) {
      if (sectorData == "NA") {
        sectorData = sectorName;
      }
      const currentData = { sector: sectorData, market: marketData };

      localStorage.setItem("currentSelection", JSON.stringify(currentData));
      dispatch(setCurrentSectorFilter(currentData));
    }
  }, [userApiData]);

  const sectorChange = async (sector: string) => {
    setSectorChangeEvent(true);
    let sectorData: any = sector;
    const defaultMarketName = userData?.userSectorData?.defaultMappings[sector];
    setSelectedMarket(defaultMarketName);
    if (sectorData) {
      if (sectorData === "NA") {
        sectorData = sectorName !== "LATAM" ? sectorName : "PBNA";
      }
    }
    setSelectedSector(sectorData);
    const getMarket: any = JSON.parse(
      localStorage.getItem(`userData-${sectorData}`) || "null"
    );
    const market = getMarket
      ? getMarket?.userSectorData?.defaultMarket
      : defaultMarket;
    const current: any = { sector: sectorData, market: defaultMarketName };

    localStorage.setItem("currentSelection", JSON.stringify(current));
    dispatch(setCurrentSectorFilter(current));

    // Reset EveryPage Data

    // Reset stastical forecast analysis page
    dispatch(
      resetForecastState({ key: "clusters", value: clusterApiData?.clusters })
    );
    dispatch(resetForecastData());
    dispatch(resetForecastDecomState());
    dispatch(resetForecastBackTestingTableState());
    dispatch(resetForecastBackTestingChartState());
    dispatch(resetForecastBackTestingAverageState());
    dispatch(resetForecastBackTestingApiDataLoading());

    // Reset Result visualization predefind page
    dispatch(
      resetResultVisualizationState({
        key: "clusters",
        value: clusterApiData?.clusters,
      })
    );
    dispatch(setLoader(false));
    dispatch(resetResultVisulizationChartData());
    dispatch(resetresultVisulizationBackTestingTableState());
    dispatch(resetPredefinePaginationState());

    // Reset Result visualization experiment page
    dispatch(resetExperId());
    dispatch(resetResultVisualizationExpState({}));
    dispatch(resetResultVisulizationExperimentBackTestingTableDataSetter());
    dispatch(resetResultVisulizationExperimentForwardForecastDataSetter());
    dispatch(resetFFChartState());

    // Reset Rca page
    dispatch(resetTreeChartData());
    dispatch(
      resetRcaState({ key: "clusters", value: clusterApiData?.clusters })
    );
    dispatch(resetMetrics());
    dispatch(resetTableData());
    dispatch(
      setRcaFilter({ key: "clusters", value: clusterApiData?.clusters })
    );
  };
  const marketChange = async (market: string) => {
    setSelectedMarket(market);

    let sector = selectedSector || defaultSector;

    let pbna_check = market && market?.includes("PBNA");
    let pfna_check = market && market?.includes("PFNA");

    if (pbna_check) {
      sector = "PBNA";
    } else if (pfna_check) {
      sector = "PFNA";
    }
    setSelectedSector(sector);
    const current = { sector, market };

    localStorage.setItem("currentSelection", JSON.stringify(current));
    dispatch(setCurrentSectorFilter(current));
  };

  return (
    <div className="flex items-center pr-[10px]">
      <select
        className="text-sm p-2 mx-1 bg-[#131313] cursor-pointer border-b-[2px] border-b-[#272727]"
        value={
          selectedSector === "PBNA" || selectedSector === "PFNA"
            ? "NA"
            : selectedSector
        }
        onChange={(e) => sectorChange(e.target.value)}
      >
        {sector?.map((data: string) => (
          <option key={data} value={data}>
            {data?.toUpperCase()}
          </option>
        ))}
      </select>

      {selectedSector !== "LATAM" && (
        <>
          <select
            className="text-sm p-2 mx-1 bg-[#131313] cursor-pointer border-b-[2px] border-b-[#272727]"
            value={selectedMarket}
            onChange={(e) => marketChange(e.target.value)}
          >
            {market?.map((data: string) => (
              <option key={data} value={data}>
                {data?.toUpperCase()}
              </option>
            ))}
          </select>
        </>
      )}
    </div>
  );
};

export default Sector;
